import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { AppIcon, AppText } from "@toothfairy/shared-ui";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ScrollView, TouchableOpacity, View } from "react-native";
import jobsHelper from "../../../../API/Jobs/jobsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import AgentBuilderSidebar from "../../../Agents/Components/AgentBuilderSidebar";
import JobsSettings from "./JobsSettings";

const HASH_ROUTED_SECTIONS = ["profile", "execution", "schedule"];

const DEFAULT_JOB_VALUES = {
  name: "",
  description: "",
  type: "TASK",
  agentID: "",
  customPromptID: "",
  timezone: "UTC",
  schedule: {
    frequency: "DAILY",
    hour: 8,
    minute: 0,
    interval: 1,
    dayOfWeek: 0,
    dayOfMonth: 1,
  },
  isActive: true,
  executionConfig: {
    timeout: 300,
    priority: "MEDIUM",
  },
  retryConfig: {
    maxRetries: 3,
    retryInterval: 60,
  },
};

const buildCreateDefaults = () => {
  const now = new Date();
  const end = new Date();
  end.setMonth(end.getMonth() + 1);
  return {
    ...DEFAULT_JOB_VALUES,
    startDate: now.toISOString(),
    endDate: end.toISOString(),
  };
};

const JobBuilderScreenContainer = () => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const history = useHistory();
  const params = useParams();
  const workspaceId = params?.workspaceId;
  const jobId = params?.jobId;
  const isEditMode = !!jobId && jobId !== "new";
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const AppToasty = AppToast.useToast();
  const formRef = useRef();
  const scrollRef = useRef();
  const [showUnsavedChoice, setShowUnsavedChoice] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const unblockRef = useRef(null);
  const { getActiveWorkspace } = useWorkspaces();

  const [activeSection, setActiveSection] = useState("profile");
  const [builderCtx, setBuilderCtx] = useState(null);
  const builderCtxRef = useRef(null);
  const [saveVersion, setSaveVersion] = useState(0);
  const [initialMapValues, setInitialMapValues] = useState(null);
  const [isJobsLoading, setIsJobsLoading] = useState(true);
  const createInitRef = useRef(false);
  const editInitRef = useRef(null);

  const loadJobs = useCallback(
    async (workspaceID) => {
      try {
        setIsJobsLoading(true);
        let resultJobs = await jobsHelper?.getJobsByWorkspaceID(workspaceID);
        resultJobs = resultJobs?.data?.cronJobsByWorkspace?.items?.filter(
          (u) => u?._deleted != true
        );
        dispatch("SET_JOBS", resultJobs || []);
      } catch (error) {
        console.error("Error loading jobs:", error);
        dispatch("SET_JOBS", []);
      } finally {
        setIsJobsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    const jobs = state?.jobs || [];
    const belongsToWorkspace = jobs.some((u) => u?.workspaceID === wsId);
    if (jobs.length === 0 || !belongsToWorkspace) {
      loadJobs(wsId);
    } else {
      setIsJobsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveWorkspace()?.id]);

  useEffect(() => {
    if (!jobId) {
      history.replace(`/workspaces/${workspaceId}/settings/jobs`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, workspaceId]);

  useEffect(() => {
    if (isEditMode) return;
    if (jobId !== "new") return;
    if (createInitRef.current) return;
    createInitRef.current = true;
    setInitialMapValues(buildCreateDefaults());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    if (isJobsLoading) return;
    if (editInitRef.current === jobId) return;
    const activeWorkspaceId = getActiveWorkspace()?.id;
    if (!activeWorkspaceId) return;
    const job = (state?.jobs || []).find((u) => u?.id === jobId);
    if (!job) {
      const listIsStale = state?.jobs?.some(
        (u) => u?.workspaceID && u?.workspaceID !== activeWorkspaceId
      );
      if (listIsStale) return;
      history.replace(`/workspaces/${activeWorkspaceId}/settings/jobs`);
      return;
    }
    editInitRef.current = jobId;
    setInitialMapValues({ ...job });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isEditMode, isJobsLoading, state?.jobs, getActiveWorkspace()?.id]);

  const isSectionVisible = (sectionId) => true;

  const sectionGroups = useMemo(() => {
    const groups = [
      {
        id: "setup",
        label: t("jobs.builderGroupSetup", { defaultValue: "Set up" }),
        items: [
          {
            id: "profile",
            title: t("jobs.builderSectionProfile", {
              defaultValue: "Overview",
            }),
            icon: AppAssets.icons.persona,
          },
          {
            id: "execution",
            title: t("jobs.builderSectionExecution", {
              defaultValue: "Execution",
            }),
            icon: AppAssets.icons.bolt,
          },
          {
            id: "schedule",
            title: t("jobs.builderSectionSchedule", {
              defaultValue: "Schedule",
            }),
            icon: AppAssets.icons.clock,
          },
        ],
      },
    ];
    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => isSectionVisible(item.id)),
      }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const applyHash = () => {
      const raw = (window.location.hash || "")
        .replace(/^#/, "")
        .trim()
        .toLowerCase();
      if (!raw) return;
      if (HASH_ROUTED_SECTIONS.includes(raw) && isSectionVisible(raw)) {
        setActiveSection(raw);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isEditMode, initialMapValues]);

  useEffect(() => {
    builderCtxRef.current = builderCtx;
  }, [builderCtx]);

  useEffect(() => {
    unblockRef.current = history.block((location) => {
      const ctxIsDirty = builderCtxRef.current?.isDirty;
      const formikDirty = !!formRef?.current?.dirty;
      const isDirty = ctxIsDirty ?? formikDirty;
      if (!isDirty) return true;
      const builderPrefix = `/workspaces/${workspaceId}/jobs/`;
      if (location?.pathname?.startsWith(builderPrefix)) return true;
      setPendingNavigation(location?.pathname);
      setShowUnsavedChoice(true);
      return false;
    });
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e) => {
      const isDirty =
        builderCtxRef.current?.isDirty ?? !!formRef?.current?.dirty;
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const handleStayOnBuilder = () => {
    setShowUnsavedChoice(false);
    setPendingNavigation(null);
  };

  const handleLeaveBuilder = () => {
    setShowUnsavedChoice(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (target) history.push(target);
  };

  const errorSections = builderCtx?.errorSections || [];
  const errorSectionsKey = JSON.stringify(errorSections);
  const prevErrorSectionsKeyRef = useRef("[]");
  useEffect(() => {
    if (prevErrorSectionsKeyRef.current === errorSectionsKey) return;
    prevErrorSectionsKeyRef.current = errorSectionsKey;
    if (errorSections.length === 0) return;
    const sectionTitles = sectionGroups
      .flatMap((g) => g.items)
      .filter((i) => errorSections.includes(i.id))
      .map((i) => i.title);
    if (sectionTitles.length === 0) return;
    setActiveSection(errorSections[0]);
    AppToasty.show(
      t("jobs.builderValidationErrors", {
        defaultValue: "Some required fields need attention in: {{sections}}",
        sections: sectionTitles.join(", "),
      }),
      { type: "danger" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorSectionsKey]);

  const lastHashTokenRef = useRef(null);
  useEffect(() => {
    const token = activeSection;
    if (!token || lastHashTokenRef.current === token) return;
    lastHashTokenRef.current = token;
    if (typeof window === "undefined") return;
    const currentHash = (window.location.hash || "")
      .replace(/^#/, "")
      .toLowerCase();
    if (currentHash === token) return;
    const { pathname, search } = window.location;
    window.history.replaceState(
      window.history.state || null,
      "",
      `${pathname}${search}#${token}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isFormReady =
    initialMapValues != null &&
    !isJobsLoading &&
    (!isEditMode || initialMapValues?.id === jobId);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
  }, [activeSection]);

  const handleJobCreated = (status, isEdit, savedId, submittedValues) => {
    if (status === "success") {
      AppToasty.show(
        isEdit
          ? t("jobs.jobSavedSuccessCaption")
          : t("jobs.jobCreationSuccessCaption"),
        { type: "success" }
      );
      formRef?.current?.resetForm?.({
        values: submittedValues || formRef.current?.values,
      });
      setSaveVersion((v) => v + 1);
      if (!isEdit && savedId) {
        createInitRef.current = false;
        history.replace(`/workspaces/${workspaceId}/jobs/${savedId}`);
      }
    } else {
      AppToasty.show(
        isEdit
          ? t("jobs.jobSaveFailedCaption")
          : t("jobs.jobCreationFailedCaption"),
        { type: "danger" }
      );
    }
  };

  const renderSaveButton = () => (
    <ToothFairyButton
      title={t("common.save")}
      onPress={() => formRef?.current?.submitForm()}
      isDisabled={!isFormReady}
      wrapperStyle={{
        minWidth: 110,
        height: 36,
        marginTop: 10,
      }}
    />
  );

  const job = useMemo(
    () => (state?.jobs || []).find((u) => u?.id === jobId),
    [state?.jobs, jobId]
  );

  const renderBody = () => {
    if (!isFormReady) {
      return (
        <View
          style={{ flex: 1, alignItems: "center", justifyContent: "center" }}
        >
          <ToothFairyLoadingPlaceholder
            caption={t("settings.list.loadingJobs")}
          />
        </View>
      );
    }
    return (
      <View style={{ flex: 1, flexDirection: "row", minHeight: 0 }}>
        <AgentBuilderSidebar
          groups={sectionGroups}
          activeSection={activeSection}
          onSelect={setActiveSection}
          errorSections={errorSections}
        />
        <ScrollView
          ref={scrollRef}
          style={{ flex: 1, minWidth: 0 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator
        >
          <JobsSettings
            key={jobId}
            ref={formRef}
            initialMapValues={initialMapValues}
            isEdit={isEditMode}
            selectedId={isEditMode ? jobId : undefined}
            opt={isEditMode ? job : undefined}
            settingType="job"
            onJobCreated={handleJobCreated}
            onJobDeleted={() =>
              history.push(`/workspaces/${workspaceId}/settings/jobs`)
            }
            isBuilderLayout
            builderActiveSection={activeSection}
            onBuilderContextChange={setBuilderCtx}
            saveVersion={saveVersion}
          />
        </ScrollView>
      </View>
    );
  };

  return (
    <View
      style={{
        flex: 1,
        height: "100%",
        backgroundColor: currentTheme?.theme?.backgroundColor,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 16,
          paddingVertical: 10,
          borderBottomWidth: 1,
          borderBottomColor: currentTheme?.theme?.lightGrey,
        }}
      >
        <TouchableOpacity
          onPress={() =>
            history.push(`/workspaces/${workspaceId}/settings/jobs`)
          }
          style={{
            flexDirection: "row",
            alignItems: "center",
            marginRight: 14,
            paddingVertical: 4,
            paddingHorizontal: 6,
          }}
        >
          <AppIcon
            icon={AppAssets.icons.arrow_left}
            size={13}
            color={currentTheme?.theme?.darkGrey}
          />
          <AppText
            style={{ marginLeft: 6, fontSize: 13 }}
            color={currentTheme?.theme?.darkGrey}
          >
            {t("settings.jobs")}
          </AppText>
        </TouchableOpacity>
        <AppText
          style={{ fontSize: 15, flexShrink: 1 }}
          fontWeight="600"
          numberOfLines={1}
        >
          {isEditMode
            ? initialMapValues?.name || job?.name || ""
            : t("jobs.builderNewJob", { defaultValue: "New job" })}
        </AppText>
        <View style={{ flex: 1 }} />
        {renderSaveButton()}
      </View>
      {renderBody()}
      <ToothFairyChoice
        cancelCaption={t("settings.qa.unsavedChangesKeepEditing", {
          defaultValue: "Keep editing",
        })}
        caption={t("settings.qa.unsavedChangesCaption", {
          defaultValue: "Unsaved changes will be lost",
        })}
        subCaption={t("jobs.unsavedChangesSubCaption", {
          defaultValue:
            "This job has changes that haven't been saved. Leave now and they're gone for good.",
        })}
        confirmCaption={t("settings.qa.leaveWithoutSaving", {
          defaultValue: "Discard changes",
        })}
        onCancelButtonPress={handleStayOnBuilder}
        onConfirmButtonPress={handleLeaveBuilder}
        setShowChoice={setShowUnsavedChoice}
        showChoice={showUnsavedChoice}
      />
    </View>
  );
};

export default JobBuilderScreenContainer;
